<!-- Hero Section -->
<header class="premium-hero">
  <div class="hero-content">
    <h1 class="hero-title">Go Premium</h1>
    <div class="premium-status-badge"
         *ngIf="isGuildContext && guild"
         [class.active]="guild?.has_premium || guild?.lifetime_premium"
         [class.lifetime]="guild?.lifetime_premium">
      <fa-icon [icon]="guild?.has_premium || guild?.lifetime_premium ? faIcons.check : faIcons.cross"></fa-icon>
      <span *ngIf="guild?.lifetime_premium">Lifetime Premium Active</span>
      <span *ngIf="!guild?.lifetime_premium && guild?.has_premium">Premium Active</span>
      <span *ngIf="!guild?.has_premium && !guild?.lifetime_premium">Premium Inactive</span>
    </div>
    <p class="hero-subtitle">
      Unlock the full potential of RaidOrganizer with Premium features. Choose the method that works best for you.
    </p>
    <div class="hero-actions" *ngIf="!isGuildContext && isLoggedIn">
      <button mat-raised-button color="primary" (click)="navigateToGuilds()" class="hero-button">
        Manage Your Servers
        <fa-icon [icon]="faIcons.arrowRight" class="ms-2"></fa-icon>
      </button>
    </div>
    <div class="hero-actions" *ngIf="!isLoggedIn">
      <button mat-raised-button color="primary" routerLink="/guilds" class="hero-button">
        Login to Get Started
        <fa-icon [icon]="faIcons.arrowRight" class="ms-2"></fa-icon>
      </button>
    </div>
  </div>
</header>

<!-- Main Content -->
<section class="premium-content">
  <div class="container-fluid">

    <!-- Context Banner (Guild Context) -->
    <div class="context-banner" *ngIf="isGuildContext && guild">
      <div class="banner-content">
        <div class="banner-icon">
          <img *ngIf="guild.icon"
               class="guild-icon"
               [src]="'https://cdn.discordapp.com/icons/' + guild.id + '/' + guild.icon + '.png?size=64'"
               [alt]="guild.name">
        </div>
        <div class="banner-text">
          <h3>Managing Premium for {{ guild.name }}</h3>
          <p>All purchases and gear exchanges are bound to this server.</p>
        </div>
      </div>
    </div>

    <!-- Premium Methods Cards -->
    <div class="premium-methods-grid">
      <div *ngFor="let method of premiumMethods"
           [id]="'premium-method-' + method.id"
           class="premium-method-card"
           [class.expanded]="method.expanded"
           [style.border-left-color]="method.color">

        <div class="method-header" (click)="toggleMethod(method)">
          <div class="method-header-content">
            <div class="method-icon-wrapper" [style.background-color]="method.color + '20'">
              <fa-icon [icon]="method.icon" [style.color]="method.color"></fa-icon>
            </div>
            <div class="method-title-group">
              <h3 class="method-title">{{ method.title }}</h3>
              <p class="method-subtitle">{{ method.subtitle }}</p>
            </div>
          </div>
          <fa-icon [icon]="method.expanded ? faIcons.chevronUp : faIcons.chevronDown"
                   class="method-chevron"
                   [ngClass]="{'rotated': method.expanded}"></fa-icon>
        </div>

        <div class="method-content" *ngIf="method.expanded">

          <!-- Gears Method -->
          <ng-container *ngIf="method.id === 'gears'">
            <div class="method-description">
              <p>{{ method.description }}</p>
              <p>You can get all Premium features for free as we don't want to exclude anyone from enjoying Premium features. All you have to do is vote for the bot!</p>
            </div>

            <div class="method-sections">
              <!-- How to Get Gears -->
              <div class="method-section">
                <h4>How do I get gears?</h4>
                <p>You can get gears by voting for the bot on these platforms:</p>
                <div class="vote-buttons">
                  <a href="https://top.gg/bot/658698521570181140" target="_blank"
                     class="vote-button"
                     [style.background-color]="method.color">
                    <fa-icon [icon]="faIcons.discord"></fa-icon>
                    <span>top.gg</span>
                  </a>
                  <a href="https://discordbotlist.com/bots/658698521570181140" target="_blank"
                     class="vote-button"
                     [style.background-color]="method.color">
                    <fa-icon [icon]="faIcons.discord"></fa-icon>
                    <span>discordbotlist.com</span>
                  </a>
                </div>
                <p class="info-text">Each vote gives you 1 gear. You can vote once per day on each platform.</p>
              </div>

              <!-- Gear Exchange (Guild Context Only) -->
              <div class="method-section" *ngIf="isGuildContext && gearInfo$">
                <h4>Gear Exchange</h4>

                <!-- User Gears -->
                <div class="gear-card" *ngIf="gearInfo$ | async as gearInfo">
                  <div class="gear-card-header">
                    <fa-icon [icon]="faIcons.gear"></fa-icon>
                    <h5>Your Gears</h5>
                  </div>
                  <div class="gear-card-content">
                    <p class="gear-count">You have <strong>{{ gearInfo.user.curr_gears }}</strong> gears</p>
                    <p class="gear-info">Transfer gears to <strong>{{ guild?.name }}</strong> to use them for Premium or limit increases.</p>

                    <div class="gear-transfer">
                      <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Transfer gears to {{ guild?.name }}</mat-label>
                        <input matInput
                               type="number"
                               [(ngModel)]="gearTransferAmount"
                               (input)="onGearUserInput($event)"
                               [max]="gearInfo.user.curr_gears"
                               min="0">
                        <mat-hint>Max: {{ gearInfo.user.curr_gears }}</mat-hint>
                      </mat-form-field>
                      <button mat-raised-button
                              color="primary"
                              (click)="contributeGears()"
                              [disabled]="gearTransferAmount <= 0 || gearTransferAmount > gearInfo.user.curr_gears">
                        Transfer
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Guild Gears -->
                <div class="gear-card" *ngIf="gearInfo$ | async as gearInfo">
                  <div class="gear-card-header">
                    <fa-icon [icon]="faIcons.gear"></fa-icon>
                    <h5>{{ guild?.name }} Gears</h5>
                  </div>
                  <div class="gear-card-content">
                    <p class="gear-count"><strong>{{ guild?.name }}</strong> has <strong>{{ gearInfo.guild.curr_gears }}</strong> gears</p>

                    <div class="alert alert-warning" *ngIf="!guild?.owner">
                      ⚠️ Only administrators can exchange gears.
                    </div>

                    <div class="gear-exchange" *ngIf="guild?.owner">
                      <p>Exchange gears for Premium features or limit increases:</p>

                      <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Select feature to exchange</mat-label>
                        <mat-select [(ngModel)]="selectedGearFeature">
                          <mat-option *ngFor="let feature of gearFeatures" [value]="feature">
                            ({{ feature.value }} Gears) {{ feature.viewValue }}
                          </mat-option>
                        </mat-select>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="full-width" *ngIf="isReminderEnabled()">
                        <mat-label>Premium expiration reminder</mat-label>
                        <mat-select [(ngModel)]="premiumReminder">
                          <mat-option [value]="undefined">Do not remind me</mat-option>
                          <mat-option *ngFor="let value of reminderOptions" [value]="value">
                            {{ value }} day(s) before expiration
                          </mat-option>
                        </mat-select>
                      </mat-form-field>

                      <button mat-raised-button
                              color="primary"
                              (click)="consumeGears()"
                              [disabled]="!selectedGearFeature || (gearInfo$ | async)?.guild.curr_gears < (selectedGearFeature?.value || 0)">
                        Exchange Gears
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- How to Use Gears (Standalone) -->
              <div class="method-section" *ngIf="!isGuildContext">
                <h4>How do I use gears?</h4>
                <p>Once you have gears, you can:</p>
                <ol>
                  <li>Login to the homepage</li>
                  <li>Go to "Manage Servers"</li>
                  <li>Select your server</li>
                  <li>Navigate to the "Premium" tab</li>
                  <li>Transfer your gears to the server and exchange them for Premium features</li>
                </ol>
                <p>You can also use the <code>/gears</code> commands in Discord. Head over to the <a href="https://discordapp.com/channels/658007285205041153/705729325915177082/707992173664010261" target="_blank">gear-exchange</a> channel on our <a href="https://discord.raidorganizer.org" target="_blank">Discord Server</a> for more information.</p>
              </div>
            </div>
          </ng-container>

          <!-- Paddle/PayPal Method -->
          <ng-container *ngIf="method.id === 'paddle'">
            <div class="method-description">
              <p>{{ method.description }}</p>
              <p>Thank you for supporting RaidOrganizer! Payments help cover server costs, development, and keep the service running.</p>
            </div>

            <div class="method-sections">
              <div class="method-section">
                <h4>Payment Information</h4>
                <ul>
                  <li>We use <strong>Paddle</strong> as our payment provider</li>
                  <li>Paddle handles payment processing, fraud protection, taxes, and multiple payment options</li>
                  <li>All payments are <strong>one-time payments</strong> (no subscription)</li>
                  <li>If you prefer a subscription model, please use <strong>Patreon</strong></li>
                  <li>Payments typically take ~10 minutes to confirm (up to 1 day for debit/credit cards)</li>
                </ul>
              </div>

              <!-- Shopping Interface (Guild Context Only) -->
              <div class="method-section" *ngIf="isGuildContext">
                <h4>Purchase Premium or Increase Limits</h4>

                <div class="lifetime-premium-notice" *ngIf="guild?.lifetime_premium">
                  <fa-icon [icon]="faIcons.check" class="notice-icon"></fa-icon>
                  <div class="notice-content">
                    <strong>Lifetime Premium Active</strong>
                    <p>This server already has Lifetime Premium. All premium features are permanently enabled.</p>
                  </div>
                </div>

                <div class="shopping-interface" [class.disabled]="guild?.lifetime_premium">
                  <div class="shop-items">
                    <!-- Premium Items -->
                    <div class="shop-category" [class.disabled]="guild?.lifetime_premium">
                      <h5>Premium Subscriptions</h5>
                      <div class="shop-item" *ngFor="let item of premiumShopItems" [class.disabled]="guild?.lifetime_premium">
                        <div class="shop-item-info">
                          <div class="shop-item-name">{{ item.viewValue }}</div>
                          <div class="shop-item-price">
                            {{ item.price }} EUR
                            <span class="price-per-month" *ngIf="item.months">
                              ({{ (item.price / item.months).toFixed(2) }} EUR/month)
                            </span>
                          </div>
                        </div>
                        <div class="shop-item-actions">
                          <button mat-icon-button
                                  color="primary"
                                  (click)="addItemToCart(item.id)"
                                  [disabled]="guild?.lifetime_premium || (item.internal === 'lifetime-premium' && serverHasLifetimePremium())">
                            <fa-icon [icon]="faIcons.shoppingCart"></fa-icon>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Limit Increases -->
                    <div class="shop-category" [class.disabled]="guild?.lifetime_premium">
                      <h5>Limit Increases</h5>
                      <p class="info-text">These increases are permanent and stay forever!</p>
                      <div class="shop-item" *ngFor="let item of limitShopItems" [class.disabled]="guild?.lifetime_premium">
                        <div class="shop-item-info">
                          <div class="shop-item-name">{{ item.viewValue }}</div>
                          <div class="shop-item-price">{{ item.price }} EUR</div>
                        </div>
                        <div class="shop-item-actions">
                          <button mat-icon-button
                                  color="primary"
                                  (click)="addItemToCart(item.id)"
                                  [disabled]="guild?.lifetime_premium">
                            <fa-icon [icon]="faIcons.shoppingCart"></fa-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Shopping Cart -->
                  <div class="shopping-cart-wrapper">
                    <div class="shopping-cart">
                      <div class="cart-header">
                        <fa-icon [icon]="faIcons.shoppingCart"></fa-icon>
                        <h5>Cart</h5>
                      </div>
                      <div class="cart-content" *ngIf="checkoutListLength > 0; else emptyCart">
                        <div class="cart-item" *ngFor="let item of checkoutList">
                          <span class="cart-item-quantity">{{ item.amount }}x</span>
                          <span class="cart-item-name">{{ item.viewValue }}</span>
                          <button mat-icon-button
                                  color="accent"
                                  (click)="removeItemFromCart(item.id)">
                            <fa-icon [icon]="faIcons.cross"></fa-icon>
                          </button>
                        </div>
                        <div class="cart-total">
                          <strong>Total: {{ priceTotal }}</strong>
                          <p class="vat-note">Price without VAT</p>
                        </div>
                      </div>
                      <ng-template #emptyCart>
                        <div class="cart-empty">Your cart is empty</div>
                      </ng-template>
                      <div class="cart-actions">
                        <button mat-raised-button
                                color="primary"
                                class="full-width"
                                [disabled]="checkoutListLength === 0"
                                (click)="checkout()">
                          Checkout
                        </button>
                        <button mat-button
                                class="full-width"
                                [disabled]="checkoutListLength === 0"
                                (click)="clearCart()">
                          Clear Cart
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="purchase-notes">
                  <p><strong>Important Notes:</strong></p>
                  <ul>
                    <li>All purchases are bound to the selected <strong>server</strong>!</li>
                    <li>All payments are one-time payments. For subscriptions, use Patreon.</li>
                    <li>Increased limits stay forever*.</li>
                    <li>All payments give you the <i>Donator</i> role on our <a href="https://discord.raidorganizer.org" target="_blank">Discord server</a>.</li>
                  </ul>
                  <p class="note-text">* "forever" means as long as the service is running and you don't delete your data.</p>
                </div>
              </div>

              <!-- Purchase Instructions (Standalone) -->
              <div class="method-section" *ngIf="!isGuildContext">
                <h4>How to Purchase</h4>
                <ol>
                  <li>Login to the webpage (top right corner)</li>
                  <li>Head over to "Manage Servers"</li>
                  <li>Choose your server from the list</li>
                  <li>Select the "Premium" tab</li>
                  <li>Add items to your cart and checkout</li>
                </ol>
                <p><i>Note:</i> You can get a 1 Week Premium trial for free at <a href="https://www.patreon.com/raid_organizer" target="_blank">Patreon</a>. Please note that after the free trial you will be charged for the next month if you do not cancel the subscription!</p>
              </div>
            </div>
          </ng-container>

          <!-- Patreon Method -->
          <ng-container *ngIf="method.id === 'patreon'">
            <div class="method-description">
              <p>{{ method.description }}</p>
              <p>Thank you for supporting RaidOrganizer! Your monthly subscription helps cover ongoing server costs, development, and ensures the service remains sustainable. With Patreon, you'll receive automatic access to all Premium features as long as your subscription remains active.</p>
            </div>

            <div class="method-sections">
              <div class="method-section">
                <h4>How to Become a Patron</h4>
                <p>Subscribe to one of our Patreon tiers to get Premium features:</p>
                <a href="https://www.patreon.com/raid_organizer" target="_blank" class="patreon-button">
                  <fa-icon [icon]="faIcons.patreon"></fa-icon>
                  <span>Subscribe on Patreon</span>
                </a>
                <p class="info-text">New patrons get a <strong>1 Week Premium trial</strong> for free! After the trial period, you'll be automatically charged monthly. You can cancel anytime before the next billing cycle.</p>
              </div>

              <div class="method-section">
                <h4>Activating Your Patreon Membership</h4>
                <p>After subscribing, Patreon will verify your payment. Once verified, RaidOrganizer will automatically send you a direct message (DM) on Discord containing your unique Patreon keys.</p>
                <p><strong>To activate Premium on your server:</strong></p>
                <ol>
                  <li>Copy the Patreon keys from the DM</li>
                  <li>Go to your server in Discord</li>
                  <li>Use the <code>/patreon set</code> command with your keys</li>
                  <li>Your server will immediately gain Premium features</li>
                </ol>
                <p>Need detailed instructions? Check out our <a href="https://docs.raidorganizer.org/premium/patreon/assign_key" target="_blank">step-by-step guide</a>.</p>
              </div>

              <div class="method-section">
                <h4>Troubleshooting</h4>
                <p>Having issues with your Patreon subscription?</p>
                <ul>
                  <li><strong>No DM received:</strong> Patreon verification can take a few minutes to 1 hour. Make sure you're on our Discord server and haven't blocked DMs from server members.</li>
                  <li><strong>Features not active:</strong> Verify that your Discord account is linked to your Patreon account on the Patreon website (Settings → Connections → Discord).</li>
                  <li><strong>Patreon role missing:</strong> Ensure you're a member of our <a href="https://discord.com/invite/cVzMUKX" target="_blank">Discord server</a>. The bot automatically assigns roles to active patrons.</li>
                  <li><strong>Keys not working:</strong> Make sure you've copied the entire key string correctly. Keys are case-sensitive and user-specific.</li>
                </ul>
                <p>If problems persist, contact our support team on Discord or check the <a href="https://docs.raidorganizer.org/premium/patreon" target="_blank">Patreon documentation</a>.</p>
              </div>

              <div class="method-section">
                <div class="alert alert-info">
                  <strong>Important:</strong> Patreon is a <strong>recurring monthly subscription</strong>. You'll be charged automatically each month unless you cancel. If you prefer a one-time payment with no recurring charges, consider using <strong>Gears</strong> (free) or <strong>One-Time Payment</strong> (PayPal) instead.
                </div>
              </div>
            </div>
          </ng-container>

        </div>
      </div>
    </div>

    <!-- Feature Comparison -->
    <div class="feature-comparison-section">
      <h2 class="section-title">What Premium Unlocks</h2>
      <p class="section-subtitle">Premium features are available via <a href="#premium-method-gears" class="premium-method-tag gears" (click)="expandAndScrollToMethod('gears'); $event.preventDefault()">⛭ Gears</a>, <a href="#premium-method-paddle" class="premium-method-tag paddle" (click)="expandAndScrollToMethod('paddle'); $event.preventDefault()">PayPal</a> or <a href="#premium-method-patreon" class="premium-method-tag patreon" (click)="expandAndScrollToMethod('patreon'); $event.preventDefault()">Patreon</a>.</p>

      <div class="comparison-table-wrapper">
        <table class="comparison-table">
          <thead>
            <tr>
              <th class="feature-column">Feature</th>
              <th class="free-column">Free</th>
              <th class="premium-column">
                <div class="premium-header-content">
                  <span class="premium-header-title">Premium</span>
                  <div class="premium-method-tags">
                    <a href="#premium-method-gears" class="premium-method-tag gears" (click)="expandAndScrollToMethod('gears'); $event.preventDefault()">⛭ Gears</a>
                    <a href="#premium-method-paddle" class="premium-method-tag paddle" (click)="expandAndScrollToMethod('paddle'); $event.preventDefault()">PayPal</a>
                    <a href="#premium-method-patreon" class="premium-method-tag patreon" (click)="expandAndScrollToMethod('patreon'); $event.preventDefault()">Patreon</a>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let category of featureCategories">
              <tr class="category-divider-row">
                <td colspan="3" class="category-divider-cell">{{ category.title }}</td>
              </tr>
              <tr *ngFor="let feature of category.features">
                <td class="feature-cell">
                  {{ feature.name }}
                  <a *ngIf="feature.docUrl" [href]="feature.docUrl" target="_blank" class="doc-link">
                    <fa-icon [icon]="faIcons.info" class="info-icon"></fa-icon>
                  </a>
                </td>
                <td class="free-cell">
                  <ng-container *ngIf="feature.free === 'cross'">
                    <fa-icon [icon]="faIcons.cross" class="cross-icon"></fa-icon>
                  </ng-container>
                  <ng-container *ngIf="feature.free !== 'cross'">{{ feature.free }}</ng-container>
                </td>
                <td class="premium-cell">
                  <ng-container *ngIf="feature.premium === 'infinity'">
                    <fa-icon [icon]="faIcons.infinity" class="infinity-icon"></fa-icon>
                  </ng-container>
                  <ng-container *ngIf="feature.premium === 'check'">
                    <fa-icon [icon]="faIcons.check" class="check-icon"></fa-icon>
                  </ng-container>
                  <ng-container *ngIf="feature.premium !== 'infinity' && feature.premium !== 'check'">
                    {{ feature.premium }}
                  </ng-container>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>

      <p class="table-note">* Default limits. You can increase them permanently with <i>gears</i> or <i>PayPal</i>.</p>
    </div>

  </div>
</section>
